import log from 'loglevel';
import { RequestManager, DefaultResponseBody } from '../browser-utilities/request-manager';
import { CommandQueue } from '../command-queue/command-queue';
import { ConfigurationManager } from '../configuration-manager/configuration-manager';
import { getBrowserContext } from '../browser-utilities/browser-context';

/**
 * The PollingManager is responsible to trigger and reschedule a continuos polling request to the API that can update configurations or trigger notifications.
 */
export class PollingManager {

  /**
   * Time between next polling calls in seconds.
   * Resets after every API call.
   * Last value is repeated for every next call.
   */
  private static pollingIntervals = [0.5, 1, 2, 3, 4, 5, 10, 15, 20, 30];
  private static intervalIndex = 0;
  private static pollingTimer: number;

  private static isPolling = false;
  private static pollingDisabled = false;

  private static get currentInterval(): number {
    if(this.intervalIndex < this.pollingIntervals.length - 1) {
      return this.pollingIntervals[this.intervalIndex++];
    } else {
      return this.pollingIntervals[this.intervalIndex];
    }
  }

  /**
   * Checks the response body to start and stop polling automatically.
   * Will be overriden if polling is always enabled via configuration
   * @param responseData DefaultResponseBody
   */
  static checkRequestStartStopPolling(responseData: DefaultResponseBody) {
    if (ConfigurationManager.get('forceEnablePolling')) {
      !this.isPolling && this.startPolling();
    } else {
      const notificationsEnabled = 
        responseData.browserNotificationEnabled ||
        responseData.exitIntentEnabled ||
        responseData.onsiteEnabled;
      if (!this.isPolling && notificationsEnabled) {
        this.startPolling();
      } else if (!notificationsEnabled) {
        this.stopPolling();
      }
    }
  }

  /**
   * resets the polling interval to 0 and schedules a polling immediately
   */
  static resetPollingInterval() {
    this.intervalIndex = 0;
    if (this.isPolling) {
      this.pollingTimer && clearTimeout(this.pollingTimer);
      this.scheduleNextPolling();
    }
  }

  /** 
   * Restarts the polling by clearing the current timeout, resetting the interval and scheduling the next polling. 
   * */
  static startPolling(): void {
    if (this.pollingDisabled) { return; }
    log.debug('starting HTTP notification polling.');
    this.isPolling = true;
    this.intervalIndex = 0;
    this.pollingTimer && clearTimeout(this.pollingTimer);
    this.scheduleNextPolling();
  }

  static stopPolling(): void {
    log.debug('stopping HTTP notification polling.');
    this.isPolling = false;
    this.pollingTimer && clearTimeout(this.pollingTimer);
  }

  static disablePolling(value: boolean) {
    this.pollingDisabled = value;
    value ? this.stopPolling() : this.startPolling();
  }

  private static scheduleNextPolling(): void {
    if (!this.isPolling) { return; }
    this.pollingTimer = setTimeout(() => {
      CommandQueue.add(() => {
        this.pollingRequest().then(
          () => this.scheduleNextPolling(),
          () => this.scheduleNextPolling()
        );
      });
    }, this.currentInterval * 1000);
  }

  /**
   * Makes a POST request to the API to poll for new configuration or onsite and notification triggering.
   */
  private static pollingRequest(): Promise<DefaultResponseBody | void>  {
    const requestbody = {
      xngGlobalUserId: ConfigurationManager.get('globalUserId'),
      xngSessionId: ConfigurationManager.get('sessionId'),
      context: getBrowserContext(),
      event: 'xng_get_messages',
      type: 'track'
    };
    log.debug(`xhr:POST polling request body: `, requestbody);
    return RequestManager.request<DefaultResponseBody>('notifications', 'POST', requestbody)
      .then(data => data && this.processPollingResponseData(data));
  }

  /**
   * Applies side effects of the polling response data before returning the response payload.
   * @param data the response data.
   */
  private static processPollingResponseData(responseData: DefaultResponseBody): DefaultResponseBody {
    ConfigurationManager.applyConfiguration({
      globalUserId: responseData.xngGlobalUserId,
      sessionId: responseData.xngSessionId
    });
    return responseData;
  }
}
